import React from 'react';
import { Wifi, WifiOff, CloudOff, RefreshCw, Check } from 'lucide-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { Button } from './ui/button';

/**
 * OfflineIndicator - Shows network status and pending sync count
 * Appears as a fixed banner when offline or has pending requests
 */
export const OfflineIndicator = () => {
  const { isOnline, isOffline, pendingCount, hasPendingRequests, triggerSync, lastSyncResult } = useNetworkStatus();

  // Don't show if online and no pending requests
  if (isOnline && !hasPendingRequests) {
    return null;
  }

  return (
    <div 
      className={`
        fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto sm:max-w-sm z-50
        rounded-lg shadow-lg border p-3 sm:p-4
        ${isOffline 
          ? 'bg-amber-950/95 border-amber-700 text-amber-200' 
          : 'bg-slate-900/95 border-slate-700 text-slate-200'
        }
        backdrop-blur-sm
        animate-in slide-in-from-bottom-2 duration-300
      `}
      role="status"
      aria-live="polite"
      data-testid="offline-indicator"
    >
      <div className="flex items-center gap-3">
        {/* Status Icon */}
        <div className={`
          flex-shrink-0 p-2 rounded-full
          ${isOffline ? 'bg-amber-800/50' : 'bg-teal-800/50'}
        `}>
          {isOffline ? (
            <WifiOff className="w-5 h-5 text-amber-400" />
          ) : hasPendingRequests ? (
            <CloudOff className="w-5 h-5 text-teal-400" />
          ) : (
            <Wifi className="w-5 h-5 text-teal-400" />
          )}
        </div>

        {/* Status Text */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">
            {isOffline ? 'You\'re offline' : 'Back online'}
          </p>
          <p className="text-xs opacity-80">
            {isOffline && hasPendingRequests && (
              <>{pendingCount} change{pendingCount !== 1 ? 's' : ''} saved locally</>
            )}
            {isOffline && !hasPendingRequests && (
              <>Changes will be saved when you reconnect</>
            )}
            {isOnline && hasPendingRequests && (
              <>Syncing {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}...</>
            )}
          </p>
        </div>

        {/* Sync Button (only when online with pending) */}
        {isOnline && hasPendingRequests && (
          <Button
            variant="ghost"
            size="sm"
            onClick={triggerSync}
            className="flex-shrink-0 h-8 px-2 text-teal-400 hover:text-teal-300 hover:bg-teal-900/50"
          >
            <RefreshCw className="w-4 h-4 animate-spin" />
          </Button>
        )}

        {/* Success indicator after sync */}
        {lastSyncResult && lastSyncResult.synced > 0 && Date.now() - lastSyncResult.timestamp < 5000 && (
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <Check className="w-3 h-3" />
            Synced
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * MiniOfflineIndicator - Small indicator for header/nav
 */
export const MiniOfflineIndicator = () => {
  const { isOffline, pendingCount } = useNetworkStatus();

  if (!isOffline && pendingCount === 0) {
    return null;
  }

  return (
    <div 
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-full text-xs
        ${isOffline ? 'bg-amber-500/20 text-amber-400' : 'bg-teal-500/20 text-teal-400'}
      `}
      title={isOffline ? 'Offline' : `${pendingCount} pending`}
    >
      {isOffline ? (
        <WifiOff className="w-3 h-3" />
      ) : (
        <CloudOff className="w-3 h-3" />
      )}
      {pendingCount > 0 && <span>{pendingCount}</span>}
    </div>
  );
};

export default OfflineIndicator;
